import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { CheckCircle2, Pause, Play, Workflow, XCircle } from 'lucide-react';
import { Typography } from 'antd';

import { StatusBadge } from './StatusBadge.js';
import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';
import type { PipelineStatus } from './types.js';

const { Text } = Typography;

export interface PipelineItem {
  id?: string;
  name: string;
  status: PipelineStatus;
}

export interface ActivePipelinesWidgetProps {
  pipelines?: readonly PipelineItem[];
  className?: string;
  testId?: string;
  loading?: boolean;
  onItemClick?: (pipeline: PipelineItem) => void;
}

function getStatusIcon(status: string): typeof Workflow {
  switch (status) {
    case 'running':
      return Play;
    case 'paused':
      return Pause;
    case 'completed':
      return CheckCircle2;
    case 'failed':
      return XCircle;
    default:
      return Workflow;
  }
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'running':
      return 'var(--ant-color-success)'; // amber/stone green success
    case 'failed':
      return 'var(--ant-color-error)'; // error red
    default:
      return 'var(--ant-color-text-tertiary)'; // stone-400 muted
  }
}

export function ActivePipelinesWidget({
  pipelines = [],
  className,
  testId,
  loading = false,
  onItemClick,
}: ActivePipelinesWidgetProps): ReactElement {
  const { t } = useTranslation();

  const widgetContainerProps = {
    title: t('crm.dashboard.pipelines.title'),
    icon: Workflow,
    iconColor: 'var(--ant-color-info)', // info blue
    minHeight: 180,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {loading ? (
        <div className="animate-pulse p-3.5 flex flex-col gap-1.5">
          {[1, 2, 3].map((index) => (
            <div
              key={index}
              className="crm-radius-s h-10 w-full"
              style={{ background: 'var(--g-fill, #f5f1eb)' }}
            />
          ))}
        </div>
      ) : pipelines.length === 0 ? (
        <div
          className="flex items-center justify-center gap-2"
          style={{ padding: '18px 16px', opacity: 0.6 }}
        >
          <Workflow size={16} style={{ color: 'var(--ant-color-text-tertiary)' }} />
          <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
            {t('crm.dashboard.pipelines.empty')}
          </span>
        </div>
      ) : (
        <div className="flex flex-col">
          {pipelines.map((pipeline) => {
            const Icon = getStatusIcon(pipeline.status);

            return (
              <button
                type="button"
                key={pipeline.id}
                onClick={onItemClick ? () => onItemClick(pipeline) : undefined}
                className="transition-colors text-left border-0 w-full crm-hover-fill"
                style={{
                  cursor: onItemClick ? 'pointer' : 'default',
                  opacity: onItemClick ? 1 : 0.7,
                  padding: '0 14px',
                  minHeight: 42,
                  borderBottom: '1px solid var(--ant-color-border-secondary)',
                  background: 'transparent',
                }}
              >
                <div className="flex w-full items-center gap-3 py-2">
                  <Icon size={14} style={{ color: getStatusColor(pipeline.status) }} />
                  <Text
                    strong
                    className="truncate flex-1"
                    style={{ fontSize: 12.5, color: 'var(--ant-color-text)' }}
                  >
                    {pipeline.name}
                  </Text>
                  <StatusBadge status={pipeline.status} size="sm" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </WidgetContainer>
  );
}
